import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiSave, FiX, FiUpload, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { AdminLayout } from '../../components/layout';
import { Card, Button, Loading } from '../../components/common';
import { createCompany, updateCompany, getCompanyById, isSlugAvailable } from '../../firebase/companies';
import { uploadCompanyLogo } from '../../firebase/storage';
import { useAuth } from '../../context/AuthContext';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const CompanyOnboard = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEditMode = !!id;
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEditMode);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [existingLogo, setExistingLogo] = useState(null);
  const [slugStatus, setSlugStatus] = useState({ checking: false, available: null });
  const logoInputRef = React.useRef(null);
  
  const [formData, setFormData] = useState({
    // Basic Info
    name: '',
    slug: '',
    description: '',
    
    // Location
    location: '',
    serviceAreas: [''],
    
    // Contact
    phone: '',
    email: '',
    whatsapp: '',
    
    // Business Details
    gstNumber: '',
    
    // Operating Hours
    operatingHours: {
      open: '09:00',
      close: '21:00',
      closedDays: []
    },
    
    // Policies
    termsAndConditions: '',
    cancellationPolicy: '',
    
    // Pricing Config
    serviceChargePercent: 5,
    serviceChargeEnabled: true,
    gstPercent: 18,
    gstEnabled: true,
    
    // Status
    status: 'active'
  });

  const [errors, setErrors] = useState({});

  // Load company data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      loadCompany();
    }
  }, [id, isEditMode]);

  const loadCompany = async () => {
    setPageLoading(true);
    try {
      const result = await getCompanyById(id);
      if (result.company) {
        const company = result.company;
        setFormData({
          name: company.name || '',
          slug: company.slug || '',
          description: company.description || '',
          location: company.location || '',
          serviceAreas: company.serviceAreas && company.serviceAreas.length > 0 ? company.serviceAreas : [''],
          phone: company.phone || '',
          email: company.email || '',
          whatsapp: company.whatsapp || '',
          gstNumber: company.gstNumber || '',
          operatingHours: company.operatingHours || {
            open: '09:00',
            close: '21:00',
            closedDays: []
          },
          termsAndConditions: company.termsAndConditions || '',
          cancellationPolicy: company.cancellationPolicy || '',
          serviceChargePercent: company.serviceChargePercent || 5,
          serviceChargeEnabled: company.serviceChargeEnabled !== undefined ? company.serviceChargeEnabled : true,
          gstPercent: company.gstPercent || 18,
          gstEnabled: company.gstEnabled !== undefined ? company.gstEnabled : true,
          status: company.status || 'active'
        });
        if (company.logo) {
          setExistingLogo(company.logo);
          setLogoPreview(company.logo);
        }
      }
    } catch (error) {
      console.error('Error loading company:', error);
      setErrors({ submit: 'Failed to load company data.' });
    } finally {
      setPageLoading(false);
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Handle slug change with availability check (skip check in edit mode if slug hasn't changed)
  const handleSlugChange = async (e) => {
    if (isEditMode) return; // Slug cannot be changed in edit mode
    
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setFormData(prev => ({ ...prev, slug: value }));
    
    if (value.length >= 3) {
      setSlugStatus({ checking: true, available: null });
      const result = await isSlugAvailable(value);
      setSlugStatus({ checking: false, available: result.available });
    } else {
      setSlugStatus({ checking: false, available: null });
    }
  };

  // Handle logo upload
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle service areas
  const handleServiceAreaChange = (index, value) => {
    const newAreas = [...formData.serviceAreas];
    newAreas[index] = value;
    setFormData(prev => ({ ...prev, serviceAreas: newAreas }));
  };

  const addServiceArea = () => {
    setFormData(prev => ({ ...prev, serviceAreas: [...prev.serviceAreas, ''] }));
  };

  const removeServiceArea = (index) => {
    const newAreas = formData.serviceAreas.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, serviceAreas: newAreas.length ? newAreas : [''] }));
  };

  // Handle closed days
  const handleClosedDayToggle = (day) => {
    const closedDays = formData.operatingHours.closedDays;
    const newClosedDays = closedDays.includes(day)
      ? closedDays.filter(d => d !== day)
      : [...closedDays, day];
    
    setFormData(prev => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        closedDays: newClosedDays
      }
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Company name is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    else if (formData.slug.length < 3) newErrors.slug = 'Slug must be at least 3 characters';
    else if (!slugStatus.available && slugStatus.available !== null) newErrors.slug = 'This slug is not available';
    
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Read values directly from DOM as fallback (for browser automation compatibility)
    const formElements = e.target.elements;
    const domFormData = {
      name: formElements.name?.value || formData.name,
      slug: formElements.slug?.value || formData.slug,
      description: formElements.description?.value || formData.description,
      location: formElements.location?.value || formData.location,
      phone: formElements.phone?.value || formData.phone,
      email: formElements.email?.value || formData.email,
      whatsapp: formElements.whatsapp?.value || formData.whatsapp,
      gstNumber: formElements.gstNumber?.value || formData.gstNumber,
      serviceChargePercent: parseFloat(formElements.serviceChargePercent?.value) || formData.serviceChargePercent,
      serviceChargeEnabled: formElements.serviceChargeEnabled?.checked ?? formData.serviceChargeEnabled,
      gstPercent: parseFloat(formElements.gstPercent?.value) || formData.gstPercent,
      gstEnabled: formElements.gstEnabled?.checked ?? formData.gstEnabled,
      operatingHours: {
        open: formElements['operatingHours.open']?.value || formData.operatingHours.open,
        close: formElements['operatingHours.close']?.value || formData.operatingHours.close,
        closedDays: formData.operatingHours.closedDays
      },
      serviceAreas: formData.serviceAreas,
      status: formData.status
    };
    
    // Update formData state with DOM values
    setFormData(prev => ({ ...prev, ...domFormData }));
    
    // Validate using DOM values
    const newErrors = {};
    if (!domFormData.name.trim()) newErrors.name = 'Company name is required';
    if (!domFormData.slug.trim()) newErrors.slug = 'Slug is required';
    else if (domFormData.slug.length < 3) newErrors.slug = 'Slug must be at least 3 characters';
    if (!domFormData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!domFormData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(domFormData.email)) newErrors.email = 'Invalid email format';
    if (!domFormData.location.trim()) newErrors.location = 'Location is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    
    try {
      let logoUrl = existingLogo; // Keep existing logo if no new file uploaded
      
      // Upload logo if a new file is provided
      if (logoFile) {
        const companyIdForUpload = isEditMode ? id : Date.now().toString(); // Use actual ID if editing, temp ID if creating
        const logoResult = await uploadCompanyLogo(logoFile, companyIdForUpload);
        if (logoResult.url) {
          logoUrl = logoResult.url;
        } else if (logoResult.error) {
          setErrors({ submit: `Failed to upload logo: ${logoResult.error}` });
          setLoading(false);
          return;
        }
      }
      
      if (isEditMode) {
        // Update existing company
        const updateData = {
          ...domFormData,
          serviceAreas: domFormData.serviceAreas.filter(area => area.trim()),
        };
        
        if (logoUrl) {
          updateData.logo = logoUrl;
        }
        
        const result = await updateCompany(id, updateData);
        
        if (result.error) {
          setErrors({ submit: result.error });
          setLoading(false);
          return;
        }
        
        // Success - redirect to company list
        navigate('/admin/companies', { 
          state: { message: `Company "${domFormData.name}" updated successfully!` }
        });
      } else {
        // Create new company
        const companyData = {
          ...domFormData,
          serviceAreas: domFormData.serviceAreas.filter(area => area.trim()),
          createdBy: user?.uid
        };
        
        if (logoUrl) {
          companyData.logo = logoUrl;
        }
        
        const result = await createCompany(companyData);
        
        if (result.error) {
          setErrors({ submit: result.error });
          setLoading(false);
          return;
        }
        
        // If logo was uploaded with temp ID, re-upload with actual company ID
        if (logoFile && result.id) {
          const logoResult = await uploadCompanyLogo(logoFile, result.id);
          if (logoResult.url) {
            await updateCompany(result.id, { logo: logoResult.url });
          }
        }
        
        // Success - redirect to company list
        navigate('/admin/companies', { 
          state: { message: `Company "${domFormData.name}" created successfully!` }
        });
      }
      
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} company:`, error);
      setErrors({ submit: `Failed to ${isEditMode ? 'update' : 'create'} company. Please try again.` });
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <AdminLayout title={isEditMode ? "Edit Company" : "Add New Company"}>
        <div className="flex items-center justify-center h-64">
          <Loading />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditMode ? "Edit Company" : "Add New Company"}>
      <form onSubmit={handleSubmit}>
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <FiAlertCircle className="text-red-500 flex-shrink-0" size={20} />
              <p className="text-red-700">{errors.submit}</p>
            </div>
          )}

          {/* Basic Information */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
            
            {/* Logo Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Logo
              </label>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo preview" className="w-full h-full object-cover" />
                  ) : (
                    <FiUpload className="text-gray-400" size={24} />
                  )}
                </div>
                <div className="flex-1">
                  <input
                    ref={logoInputRef}
                    type="file"
                    id="logo"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => logoInputRef.current?.click()}
                  >
                    {logoPreview ? 'Change Logo' : 'Upload Logo'}
                  </Button>
                  {logoFile && (
                    <button
                      type="button"
                      onClick={() => {
                        setLogoFile(null);
                        setLogoPreview(existingLogo || null);
                        if (logoInputRef.current) {
                          logoInputRef.current.value = '';
                        }
                      }}
                      className="ml-2 text-sm text-red-600 hover:text-red-700"
                    >
                      Cancel
                    </button>
                  )}
                  <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., ABC Car Rentals"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL Slug *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleSlugChange}
                    placeholder="e.g., abc-rentals"
                    disabled={isEditMode}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent pr-10 ${
                      errors.slug ? 'border-red-500' : 'border-gray-300'
                    } ${isEditMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                  {slugStatus.checking && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loading size="sm" />
                    </div>
                  )}
                  {!slugStatus.checking && slugStatus.available === true && (
                    <FiCheck className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />
                  )}
                  {!slugStatus.checking && slugStatus.available === false && (
                    <FiX className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.slug ? `${formData.slug}.carbooking.com` : 'your-slug.carbooking.com'}
                </p>
                {errors.slug && <p className="text-red-500 text-sm mt-1">{errors.slug}</p>}
              </div>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                placeholder="Brief description of the company..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </Card>

          {/* Location */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Location & Service Areas</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Primary Location (City) *
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="e.g., Mumbai, Maharashtra"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.location ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service Areas
              </label>
              {formData.serviceAreas.map((area, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={area}
                    onChange={(e) => handleServiceAreaChange(index, e.target.value)}
                    placeholder="e.g., South Mumbai"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {formData.serviceAreas.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeServiceArea(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <FiX size={20} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addServiceArea}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                + Add another area
              </button>
            </div>
          </Card>

          {/* Contact Information */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 9876543210"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  WhatsApp Number
                </label>
                <input
                  type="tel"
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleChange}
                  placeholder="+91 9876543210"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="contact@company.com"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GST Number
                </label>
                <input
                  type="text"
                  name="gstNumber"
                  value={formData.gstNumber}
                  onChange={handleChange}
                  placeholder="27AAAAA0000A1Z5"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </Card>

          {/* Operating Hours */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Operating Hours</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Opening Time
                </label>
                <input
                  type="time"
                  name="operatingHours.open"
                  value={formData.operatingHours.open}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Closing Time
                </label>
                <input
                  type="time"
                  name="operatingHours.close"
                  value={formData.operatingHours.close}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Closed Days
              </label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => handleClosedDayToggle(day)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      formData.operatingHours.closedDays.includes(day)
                        ? 'bg-red-100 text-red-700 border border-red-200'
                        : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          </Card>

          {/* Pricing Configuration */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing Configuration</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Service Charge</p>
                  <p className="text-sm text-gray-500">Additional service fee on bookings</p>
                </div>
                <div className="flex items-center gap-4">
                  <input
                    type="number"
                    name="serviceChargePercent"
                    value={formData.serviceChargePercent}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center"
                  />
                  <span className="text-gray-500">%</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="serviceChargeEnabled"
                      checked={formData.serviceChargeEnabled}
                      onChange={handleChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">GST</p>
                  <p className="text-sm text-gray-500">Goods and Services Tax</p>
                </div>
                <div className="flex items-center gap-4">
                  <input
                    type="number"
                    name="gstPercent"
                    value={formData.gstPercent}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center"
                  />
                  <span className="text-gray-500">%</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="gstEnabled"
                      checked={formData.gstEnabled}
                      onChange={handleChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>
              </div>
            </div>
          </Card>

          {/* Status */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Status</h3>
            
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="active"
                  checked={formData.status === 'active'}
                  onChange={handleChange}
                  className="w-4 h-4 text-primary-600"
                />
                <span className="text-gray-700">Active</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="inactive"
                  checked={formData.status === 'inactive'}
                  onChange={handleChange}
                  className="w-4 h-4 text-primary-600"
                />
                <span className="text-gray-700">Inactive</span>
              </label>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/companies')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2"
            >
              {loading ? <Loading size="sm" /> : <FiSave size={18} />}
              {isEditMode ? 'Update Company Details' : 'Create Company'}
            </Button>
          </div>
        </div>
      </form>
    </AdminLayout>
  );
};

export default CompanyOnboard;
